/** Print an isolated snapshot, preserving vector artwork and leaving the app usable on cancel. */
export async function printElement(content: HTMLElement, title: string): Promise<void> {
  const frame = document.createElement('iframe');
  frame.title = title;
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;width:1000px;height:800px;left:-10000px;border:0';
  document.body.append(frame);
  const target = frame.contentDocument;
  const view = frame.contentWindow;
  if (!target || !view) {
    frame.remove();
    throw new Error('Print frame unavailable');
  }
  try {
    target.title = title;
    target.documentElement.lang = document.documentElement.lang;
    target.documentElement.dataset.themeMode = 'light';
    const base = target.createElement('base');
    base.href = document.baseURI;
    target.head.append(base);
    const styles = [...document.querySelectorAll('style, link[rel="stylesheet"]')].map((node) => {
      const copy = node.cloneNode(true) as HTMLElement;
      const loaded =
        copy.tagName === 'LINK'
          ? new Promise<void>((resolve, reject) => {
              copy.onload = () => resolve();
              copy.onerror = () => reject(new Error('Print stylesheet unavailable'));
            })
          : Promise.resolve();
      target.head.append(copy);
      return loaded;
    });
    const style = target.createElement('style');
    style.textContent = `
      @page { margin: 12mm; }
      :root { color-scheme: light; }
      html, body { background: white !important; color: #1f2933 !important; margin: 0; }
      body { padding: 0 !important; }
      .print-document { width: 100%; max-width: none; }
      .print-document button:not(.properties-table-toggle), .monograph-hero-actions,
      .monograph-sheet-contents, #monograph-discussion, [role="dialog"], form { display: none !important; }
      .properties-table-toggle { border: 0; padding: 0; background: none; color: inherit; text-align: inherit; }
      .properties-table-toggle svg { display: none; }
      .monograph-sheet-body, .monograph-hero-inner { display: block !important; }
      .monograph-hero-picture { float: left; margin: 0 8mm 4mm 0; }
      .monograph-hero { overflow: auto; break-inside: avoid; }
      .monograph-sheet-sections, .properties-table-scroll { overflow: visible !important; min-width: 0; }
      .monograph-sheet { border: 0; box-shadow: none; width: 100%; }
      .monograph-sheet-identifiers code { overflow-wrap: anywhere; }
      table { width: 100%; min-width: 0 !important; }
      .properties-table th, .properties-table td { padding: 2mm !important; overflow-wrap: anywhere; white-space: normal; } tr, figure { break-inside: avoid; } thead { display: table-header-group; }
      h2, h3 { break-after: avoid; }
      .print-drawing { display: block; width: 100%; height: auto; max-height: 250mm; object-fit: contain; }
    `;
    target.head.append(style);
    const snapshot = content.cloneNode(true) as HTMLElement;
    snapshot.classList.add('print-document');
    snapshot.removeAttribute('hidden');
    target.body.append(snapshot);
    await Promise.all(styles);
    await target.fonts?.ready;
    await Promise.all([...target.images].map((img) => img.decode()));
    await new Promise<void>((resolve) => view.requestAnimationFrame(() => resolve()));
    // Keep the frame alive until the browser finishes printing (including cancellation).
    view.addEventListener('afterprint', () => frame.remove(), { once: true });
    view.focus();
    view.print();
  } catch (error) {
    frame.remove();
    throw error;
  }
}
